import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { favoriteTools as initialFavorites, recommendedTools } from "@/data/profileData";

export default function Profile() {
  const [favorites, setFavorites] = useState(initialFavorites);
  const [newCategory, setNewCategory] = useState("");
  const [editingCategory, setEditingCategory] = useState<number | null>(null);
  const [tempCategory, setTempCategory] = useState("");

  const categories = [...new Set(favorites.map(tool => tool.category))];

  const removeFavorite = (id: number) => {
    setFavorites(favorites.filter(tool => tool.id !== id));
    toast.success("Tool removed from favorites");
  };

  const addCategory = () => {
    if (newCategory.trim()) {
      const newCategories = [...categories, newCategory.trim()];
      toast.success("Category added");
      setNewCategory("");
    }
  };

  const startEditCategory = (category: string, index: number) => {
    setEditingCategory(index);
    setTempCategory(category);
  };

  const saveEditCategory = () => {
    if (editingCategory !== null && tempCategory.trim()) {
      const updatedFavorites = favorites.map(tool => {
        if (tool.category === categories[editingCategory]) {
          return { ...tool, category: tempCategory.trim() };
        }
        return tool;
      });
      setFavorites(updatedFavorites);
      setEditingCategory(null);
      toast.success("Category updated");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-8">My Profile</h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Favorites Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">My Favorites</h2>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="New category"
                    className="px-3 py-1 border rounded"
                  />
                  <button
                    onClick={addCategory}
                    className="bg-[#1E90FF] text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>

              {favorites.length === 0 ? (
                <div className="text-center py-8">
                  <i className="fa-regular fa-bookmark text-4xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">No favorite tools yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {categories.map((category, index) => (
                    <div key={index} className="mb-6">
                      <div className="flex items-center justify-between mb-2">
                        {editingCategory === index ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={tempCategory}
                              onChange={(e) => setTempCategory(e.target.value)}
                              className="px-2 py-1 border rounded"
                            />
                            <button
                              onClick={saveEditCategory}
                              className="text-[#1E90FF] hover:text-blue-700"
                            >
                              <i className="fa-solid fa-check"></i>
                            </button>
                          </div>
                        ) : (
                          <>
                            <h3 className="text-lg font-semibold">{category}</h3>
                            <button
                              onClick={() => startEditCategory(category, index)}
                              className="text-gray-500 hover:text-[#1E90FF]"
                            >
                              <i className="fa-solid fa-pen-to-square"></i>
                            </button>
                          </>
                        )}
                      </div>
                      <div className="space-y-2">
                        {favorites
                          .filter(tool => tool.category === category)
                          .map(tool => (
                            <div
                              key={tool.id}
                              className="flex justify-between items-center bg-gray-50 p-3 rounded-lg"
                            >
                              <Link
                                to={`/tools/${tool.id}`}
                                className="hover:text-[#1E90FF]"
                              >
                                {tool.name}
                              </Link>
                              <button
                                onClick={() => removeFavorite(tool.id)}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recommendations Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold mb-6">Recommended For You</h2>
              {recommendedTools.length === 0 ? (
                <div className="text-center py-8">
                  <i className="fa-regular fa-lightbulb text-4xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">
                    No recommendations yet. Start exploring tools to get personalized recommendations.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recommendedTools.map(tool => (
                    <Link
                      key={tool.id}
                      to={`/tools/${tool.id}`}
                      className="block bg-gray-50 hover:bg-gray-100 p-4 rounded-lg transition-colors"
                    >
                      <h3 className="font-medium mb-1">{tool.name}</h3>
                      <p className="text-sm text-gray-500">{tool.reason}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
